/**
 * BotManager - dono dos inimigos: pool, spawn, orcamento de CPU e coordenacao
 * de esquadrao.
 *
 * Tres coisas nao obvias moram aqui:
 *
 * 1. ORCAMENTO. Percepcao (raycast de linha de visao) e busca de caminho (A*)
 *    sao caras. Rodar isso para 12 bots a 120 Hz derruba o frame. Entao o
 *    cerebro roda em RODIZIO: 2 bots por passo fixo, o que da ~20 Hz de decisao
 *    por bot com 12 vivos - imperceptivel no jogo, 6x mais barato. O movimento e
 *    o disparo, que sao contas simples, rodam para todos em todo passo.
 *    Busca de caminho tem orcamento proprio: no maximo UMA por passo, global.
 *
 * 2. COORDENACAO. Sem coordenacao, todo bot toma a mesma decisao ao mesmo tempo
 *    e o pelotao vira uma fila indiana entrando no mesmo corredor. Aqui os bots
 *    recebem papeis (assalto / flanco / supressao), o flanco alterna de lado, e
 *    ponto de cobertura ja reservado por um companheiro nao e oferecido a outro.
 *
 * 3. LIMPEZA. Bot morto SAI de ctx.targets na hora (senao a balistica continua
 *    acertando cadaver e a contagem de inimigos nunca zera) e o corpo volta para
 *    o pool depois de alguns segundos. Vazamento aqui trava a partida.
 */

import * as THREE from 'three';
import { GAME } from '../core/Constants.js';
import { Bot, BOT_STATE } from './Bot.js';
import { Navmesh } from './Navmesh.js';
import { disposeSharedBotGeometry } from './BotModel.js';

/** Cerebros processados por passo fixo. 2 a 120 Hz = 240 decisoes/s no total. */
const THINK_PER_STEP = 2;
/** Nenhum bot pode passar disso sem pensar, mesmo com a fila cheia. */
const MAX_THINK_GAP = 0.25;
/** Segundos que o cadaver fica no chao antes de afundar e sumir. */
const CORPSE_TIME = 5.5;
const CORPSE_SINK = 1.6;
/**
 * Cadaver custa o mesmo que um bot vivo: 3 malhas (corpo em dois SkinnedMesh
 * mais o fuzil), ou 9 chamadas contando shadow map e NormalPass. Ficou barato,
 * mas o teto continua 4 - o limite aqui e de leitura da cena, nao de render:
 * mais corpos que isso no chao viram entulho e escondem quem esta vivo.
 */
const MAX_CORPSES = 4;
/** Distancia minima do jogador para nascer. Inimigo brotando na cara e trapaça. */
const SPAWN_MIN_DIST = 20;
const SPAWN_IDEAL_DIST = 34;
/** Alcance de audicao. */
const HEAR_GUNSHOT = 62;
const HEAR_RUN = 19;
const HEAR_WALK = 9;
/** Raio em que o grito de alerta espalha a informacao para o esquadrao. */
const SHOUT_RADIUS = 28;

/**
 * SOMBRA DE CONTATO (o que colava o inimigo na foto).
 *
 * A sombra direcional do sol resolve o bot em area iluminada e SO ELA. Dentro de
 * uma rua ja sombreada - metade do mapa no fim de tarde - a luz do sol nao chega,
 * a sombra projetada nao existe e o SSAO nao alcanca o vao de 2 cm entre a sola e
 * o asfalto. Resultado medido no print 13: os pixels do chao colados na bota tem
 * exatamente o mesmo brilho dos pixels a 20 cm dali. Sem gradiente de contato o
 * olho nao tem como saber a que altura o objeto esta, e le "adesivo".
 *
 * O conserto e o truque classico do genero: um quad no chao com gradiente radial
 * no ALFA, preto puro, sem escrita de profundidade. Como a cor e preta, misturar
 * com alfa `a` equivale a multiplicar a cena por (1 - a) - ou seja, e uma oclusao
 * de verdade, nao um borrao cinza colado por cima.
 *
 * Custo: UM InstancedMesh para o pool inteiro = 1 draw call, 2 triangulos por bot.
 */
const SHADOW_RADIUS = 0.46;      // meia largura do decalque (0.92 m de lado)
const SHADOW_LONG = 1.18;        // um pouco alongado no eixo do corpo
const SHADOW_LIFT = 0.022;       // ergue do chao para nao brigar com o z-buffer
const SHADOW_ALPHA = 0.42;       // opacidade no centro
const SHADOW_AIR = 1.7;          // altura em que o bot no ar perde a sombra
const SHADOW_TEX = 64;

const _v1 = new THREE.Vector3();
const _v2 = new THREE.Vector3();

// temporarios do decalque (zero alocacao por frame)
const _sPos = new THREE.Vector3();
const _sScale = new THREE.Vector3();
const _sNormal = new THREE.Vector3();
const _sQuat = new THREE.Quaternion();
const _sYaw = new THREE.Quaternion();
const _sMat = new THREE.Matrix4();
const _sUp = new THREE.Vector3(0, 1, 0);
const _sZero = new THREE.Vector3(0, 0, 0);
const _sIdent = new THREE.Quaternion();

/**
 * Gradiente radial no alfa. Nucleo cheio ate 40% e queda longa depois: borda
 * dura vira disco preto, queda linear pura vira bolha difusa que nao ancora nada.
 */
/** Decalque nao participa de nenhum raycast (bala, mira, depuracao). */
function noRaycast() { /* proposital: nada */ }

function makeShadowTexture() {
  const c = document.createElement('canvas');
  c.width = SHADOW_TEX;
  c.height = SHADOW_TEX;
  const g = c.getContext('2d');
  const h = SHADOW_TEX * 0.5;
  const grd = g.createRadialGradient(h, h, 0, h, h, h);
  grd.addColorStop(0.00, 'rgba(0,0,0,1)');
  grd.addColorStop(0.40, 'rgba(0,0,0,0.86)');
  grd.addColorStop(0.68, 'rgba(0,0,0,0.34)');
  grd.addColorStop(0.86, 'rgba(0,0,0,0.09)');
  grd.addColorStop(1.00, 'rgba(0,0,0,0)');
  g.fillStyle = grd;
  g.fillRect(0, 0, SHADOW_TEX, SHADOW_TEX);
  const t = new THREE.CanvasTexture(c);
  t.wrapS = THREE.ClampToEdgeWrapping;
  t.wrapT = THREE.ClampToEdgeWrapping;
  return t;
}

export class BotManager {
  constructor(ctx) {
    this.ctx = ctx;
    /** @type {Bot[]} pool completo (ativos e reciclaveis) */
    this.bots = [];
    /** @type {Bot[]} somente vivos - devolvido por getBots(), nao modificar de fora */
    this.aliveList = [];
    this.navmesh = new Navmesh(ctx);

    this.enabled = true;
    this.time = 0;
    this.pathBudget = 1;

    this._cursor = 0;
    this._squadTimer = 0;
    this._pendingDeaths = [];
    this._corpses = [];
    this._offs = [];
    this._sorted = [];
    this._flankSide = 1;
    this._errorCount = 0;
    this._warned = false;

    // decalque de sombra de contato (1 draw call para o pool inteiro)
    this._shadowMesh = null;
    this._shadowTex = null;
    this._shadowCap = 0;

    this._cmpDist = (a, b) => a._sortKey - b._sortKey;
  }

  // =========================================================================
  // ciclo de vida
  // =========================================================================

  async init() {
    await this.navmesh.init();
    this._bindEvents();
    // pre-constroi o pool: montar humanoide durante a partida daria engasgo
    const pool = Math.min(GAME.MAX_ALIVE_ENEMIES, 12);
    for (let i = 0; i < pool; i++) this._create();
    this._buildShadows();
    return this;
  }

  /**
   * Monta o InstancedMesh das sombras de contato. Falhar aqui (sem canvas, sem
   * cena) so custa a sombra: o resto do inimigo continua funcionando.
   */
  _buildShadows() {
    const scene = this.ctx.scene;
    if (!scene) return;
    try {
      const cap = Math.max(4, GAME.MAX_ALIVE_ENEMIES + MAX_CORPSES + 4);
      const geo = new THREE.PlaneGeometry(1, 1).rotateX(-Math.PI / 2);
      this._shadowTex = makeShadowTexture();
      const mat = new THREE.MeshBasicMaterial({
        map: this._shadowTex,
        color: 0xffffff,
        transparent: true,
        opacity: SHADOW_ALPHA,
        depthWrite: false,
        // Sem neblina DE PROPOSITO: a neblina mexeria na COR do decalque (que e
        // preta) e nao no alfa - de longe a sombra viraria uma mancha CLARA no
        // chao, que e exatamente o oposto do que ela existe para fazer.
        fog: false,
        polygonOffset: true,
        polygonOffsetFactor: -2,
        polygonOffsetUnits: -2
      });

      const mesh = new THREE.InstancedMesh(geo, mat, cap);
      mesh.name = 'botContactShadows';
      mesh.frustumCulled = false;     // as instancias cobrem o mapa inteiro
      mesh.castShadow = false;
      mesh.receiveShadow = false;
      mesh.renderOrder = -1;          // vai antes de fumaca, tracer e clarao
      mesh.matrixAutoUpdate = false;
      mesh.raycast = noRaycast;       // decalque nao e alvo de bala nem de mira

      // tudo escondido no quadro zero: quad piscando antes do primeiro spawn e feio
      _sMat.compose(_sZero, _sIdent, _sZero);
      for (let i = 0; i < cap; i++) mesh.setMatrixAt(i, _sMat);
      mesh.count = 0;
      mesh.instanceMatrix.needsUpdate = true;

      scene.add(mesh);
      this._shadowMesh = mesh;
      this._shadowCap = cap;
    } catch (err) {
      this._shadowMesh = null;
      console.warn('[BotManager] sombra de contato indisponivel', err);
    }
  }

  _create() {
    const bot = new Bot(this.ctx, this);
    bot.addToScene(this.ctx.scene);
    bot.model.setVisible(false);
    bot._lastThink = 0;
    bot._errors = 0;
    this.bots.push(bot);
    return bot;
  }

  _bindEvents() {
    const ev = this.ctx.EVENTS;
    const bus = this.ctx.events;
    if (!bus) return;

    this._offs.push(bus.on(ev.WEAPON_FIRE, (p) => {
      // WEAPON_FIRE e do jogador (bot emite AI_FIRE): tiro entrega a posicao
      const pos = p?.origin || this.ctx.player?.position;
      if (pos) this._broadcastNoise(pos.x, pos.y, pos.z, HEAR_GUNSHOT, 1.0);
    }));

    this._offs.push(bus.on(ev.FOOTSTEP, (p) => {
      if (!p || p.isPlayer === false || !p.position) return;
      const range = p.running ? HEAR_RUN : HEAR_WALK;
      this._broadcastNoise(p.position.x, p.position.y, p.position.z, range, 0.45);
    }));

    this._offs.push(bus.on(ev.ENTITY_KILLED, (p) => {
      // dedupe: se a balistica ja anunciou a morte, o manager nao repete
      const e = p?.entity;
      if (e && e.isBot) e.killEventSeen = true;
    }));

    this._offs.push(bus.on(ev.PLAYER_DIED, () => {
      for (const b of this.aliveList) {
        b.canSee = false;
        b.seeTime = 0;
        b.alerted = false;
        b.hasLastKnown = false;
        if (b.state !== BOT_STATE.DEAD) b._setState(BOT_STATE.PATROL);
      }
    }));

    this._offs.push(bus.on(ev.GAME_STATE, (p) => {
      // voltar ao menu limpa a arena; 'gameover'/'victory' quem decide e o GameState
      if (p?.state === 'menu') this.killAll();
    }));
  }

  dispose() {
    for (const off of this._offs) { try { off(); } catch (err) { /* ignorado */ } }
    this._offs.length = 0;
    for (const b of this.bots) {
      try { b.dispose(); } catch (err) { /* ignorado */ }
    }
    this.bots.length = 0;
    this.aliveList.length = 0;

    if (this._shadowMesh) {
      this._shadowMesh.parent?.remove(this._shadowMesh);
      this._shadowMesh.geometry.dispose();
      this._shadowMesh.material.dispose();
      this._shadowMesh.dispose();
      this._shadowMesh = null;
    }
    if (this._shadowTex) { this._shadowTex.dispose(); this._shadowTex = null; }

    this.navmesh.dispose();
    disposeSharedBotGeometry();
  }

  // =========================================================================
  // API publica
  // =========================================================================

  /** Numero de inimigos VIVOS. */
  count() {
    return this.aliveList.length;
  }

  /** Lista de bots vivos. Somente leitura - e o array interno, nao uma copia. */
  getBots() {
    return this.aliveList;
  }

  /**
   * Nasce n inimigos em spawnPoints.enemy longe do jogador.
   * Respeita GAME.MAX_ALIVE_ENEMIES. Devolve quantos realmente nasceram.
   */
  spawn(n) {
    let spawned = 0;
    const limit = GAME.MAX_ALIVE_ENEMIES;
    for (let i = 0; i < n; i++) {
      if (this.aliveList.length >= limit) break;
      const bot = this._acquire();
      if (!bot) break;
      if (!this._pickSpawn(_v1)) break;
      bot.spawnAt(_v1.x, _v1.y, _v1.z);
      bot._shadowY = _v1.y;   // ja tem chao valido no primeiro quadro
      bot._lastThink = this.time;
      bot._errors = 0;
      bot._claimTime = 0;   // reserva de cobertura do uso anterior nao vale mais
      this.aliveList.push(bot);
      spawned++;
    }
    return spawned;
  }

  /**
   * Mata (na verdade REMOVE) todos os inimigos. Nao emite ENTITY_KILLED de
   * proposito: e limpeza de arena (reset de onda, volta ao menu), nao abate -
   * emitir daria pontos de graca ao jogador e bagunçaria a contagem da onda.
   */
  killAll() {
    let n = 0;
    for (const b of this.bots) {
      if (!b.active) continue;
      b.despawn();
      n++;
    }
    this.aliveList.length = 0;
    this._corpses.length = 0;
    this._pendingDeaths.length = 0;
    return n;
  }

  _acquire() {
    for (const b of this.bots) if (!b.active) return b;
    if (this.bots.length < GAME.MAX_ALIVE_ENEMIES + MAX_CORPSES + 2) return this._create();
    // pool cheio de cadaver: recicla o mais antigo
    if (this._corpses.length > 0) {
      const old = this._corpses.shift();
      old.bot.despawn();
      return old.bot;
    }
    return null;
  }

  /** Escolhe um ponto de nascimento longe do jogador e ainda nao usado. */
  _pickSpawn(out) {
    const points = this.ctx.level?.spawnPoints?.enemy;
    const ppos = this.ctx.player?.position || this.ctx.controller?.position;

    if (Array.isArray(points) && points.length > 0) {
      let best = null;
      let bestScore = -Infinity;
      let secondBest = null;
      for (let i = 0; i < points.length; i++) {
        const p = points[i];
        if (!p || !Number.isFinite(p.x)) continue;
        let score = 0;
        if (ppos) {
          const dx = p.x - ppos.x, dz = p.z - ppos.z;
          const d = Math.sqrt(dx * dx + dz * dz);
          if (d < SPAWN_MIN_DIST) score -= (SPAWN_MIN_DIST - d) * 12;
          score -= Math.abs(d - SPAWN_IDEAL_DIST) * 0.6;
        }
        // ponto usado ha pouco: evita dois bots saindo do mesmo lugar
        const last = this._spawnUse(i);
        if (this.time - last < 4) score -= 40;
        score += Math.random() * 6;   // desempate: nao virar sempre a mesma rota
        if (score > bestScore) { secondBest = best; bestScore = score; best = i; }
      }
      const chosen = (best !== null && Math.random() < 0.75) ? best : (secondBest !== null ? secondBest : best);
      if (chosen !== null) {
        const p = points[chosen];
        this._spawnUse(chosen, this.time);
        out.set(
          p.x + (Math.random() - 0.5) * 1.6,
          p.y + 0.05,
          p.z + (Math.random() - 0.5) * 1.6
        );
        this._snapToGround(out);
        return true;
      }
    }

    // sem spawnPoints: usa a propria navegacao, longe do jogador
    if (ppos && Number.isFinite(ppos.x) && this.navmesh.ready) {
      for (let tries = 0; tries < 12; tries++) {
        const p = this.navmesh.randomPoint(ppos, 38, _v2);
        if (!p) break;
        const dx = p.x - ppos.x, dz = p.z - ppos.z;
        if (dx * dx + dz * dz < SPAWN_MIN_DIST * SPAWN_MIN_DIST) continue;
        out.copy(p);
        out.y += 0.05;
        return true;
      }
    }

    // Ultimo recurso: qualquer ponto navegavel perto do centro do mapa. Existe
    // para o __GAME__.spawnBots() da revisao visual funcionar mesmo antes de
    // haver partida (sem isso a tela de inspecao sai vazia de inimigos).
    if (this.navmesh.ready) {
      _v2.set(
        this.navmesh.minX + (this.navmesh.cols * this.navmesh.cell) * 0.5,
        0,
        this.navmesh.minZ + (this.navmesh.rows * this.navmesh.cell) * 0.5
      );
      const p = this.navmesh.randomPoint(_v2, 30, _v2);
      if (p) { out.copy(p); out.y += 0.05; return true; }
    }
    return false;
  }

  _spawnUse(index, setTime) {
    if (!this._spawnTimes) this._spawnTimes = [];
    if (setTime !== undefined) { this._spawnTimes[index] = setTime; return setTime; }
    return this._spawnTimes[index] === undefined ? -99 : this._spawnTimes[index];
  }

  _snapToGround(v) {
    const col = this.ctx.collision;
    if (col && typeof col.sampleGround === 'function') {
      try {
        const g = col.sampleGround(v.x, v.z, v.y + 30);
        if (g && Number.isFinite(g.y)) { v.y = g.y + 0.05; return; }
      } catch (err) { /* cai no plano B */ }
    }
    const h = this.navmesh.heightAt(v.x, v.z);
    if (Number.isFinite(h)) v.y = h + 0.05;
  }

  // =========================================================================
  // servicos para os bots
  // =========================================================================

  /** Orcamento global de busca de caminho: uma por passo fixo. */
  consumePathBudget() {
    if (this.pathBudget <= 0) return false;
    this.pathBudget--;
    return true;
  }

  /**
   * Reserva de cobertura: dois bots correndo para o mesmo canto ficam presos um
   * no outro e viram alvo facil. Aqui cada um pega um ponto diferente.
   */
  claimCover(bot, list) {
    if (!list || list.length === 0) return null;
    const now = this.time;
    // A reserva mora NO PROPRIO BOT (um ponto por bot): sem lista dinamica,
    // sem splice e sem alocar objeto dentro do passo fixo.
    for (let i = 0; i < list.length; i++) {
      const p = list[i];
      let taken = false;
      for (let k = 0; k < this.aliveList.length; k++) {
        const o = this.aliveList[k];
        if (o === bot || !o._claimTime || now - o._claimTime > 8) continue;
        const dx = o._claimX - p.x, dz = o._claimZ - p.z;
        if (dx * dx + dz * dz < 5.0) { taken = true; break; }
      }
      if (!taken) {
        bot._claimX = p.x;
        bot._claimZ = p.z;
        bot._claimTime = now;
        return p;
      }
    }
    return list[0];
  }

  /** Um bot viu ou levou tiro: espalha a informacao para quem esta perto. */
  reportContact(bot, position, sure) {
    if (!position) return;
    for (const other of this.aliveList) {
      if (other === bot || !other.alive || other.canSee) continue;
      const dx = other.position.x - bot.position.x;
      const dz = other.position.z - bot.position.z;
      if (dx * dx + dz * dz > SHOUT_RADIUS * SHOUT_RADIUS) continue;
      other.hear(position.x, position.y, position.z, sure ? 0.8 : 0.55);
    }
  }

  _broadcastNoise(x, y, z, range, strength) {
    const r2 = range * range;
    for (const b of this.aliveList) {
      const dx = b.position.x - x;
      const dz = b.position.z - z;
      const d2 = dx * dx + dz * dz;
      if (d2 > r2) continue;
      // som perde nitidez com a distancia: longe, o bot so sabe a direcao geral
      const s = strength * (1 - Math.sqrt(d2) / range) * 0.9 + 0.1;
      b.hear(x, y, z, Math.min(1, s));
    }
  }

  /** Chamado pelo proprio Bot quando morre. */
  onBotDied(bot, source, headshot) {
    const idx = this.aliveList.indexOf(bot);
    if (idx >= 0) this.aliveList.splice(idx, 1);
    this._pendingDeaths.push({ bot, source, headshot, at: this.time });
    this._corpses.push({ bot, at: this.time });
    while (this._corpses.length > MAX_CORPSES) {
      // sempre remove de fato: sair da lista sem despawn deixaria o corpo
      // ativo para sempre, ocupando slot do pool
      this._corpses.shift().bot.despawn();
    }
    this.ctx.events?.emit?.(this.ctx.EVENTS.SFX, {
      name: 'enemy_death', position: bot.position, volume: 0.9, pitch: 0.9 + Math.random() * 0.2
    });
  }

  /**
   * Emite ENTITY_KILLED se ninguem mais emitiu. A balistica tambem anuncia a
   * morte (secao 4.12), entao esperamos um passo: se o evento apareceu, ficamos
   * quietos. Emitir duas vezes dobraria pontuacao e contagem de onda.
   */
  _flushDeaths() {
    for (let i = this._pendingDeaths.length - 1; i >= 0; i--) {
      const d = this._pendingDeaths[i];
      // espera virar o passo: a balistica emite de forma sincrona durante o
      // dano, entao um passo ja basta para saber se ela emitiu (e nao atrasa
      // a contagem de onda de forma perceptivel: 8 ms)
      if (this.time - d.at < 1e-6) continue;
      this._pendingDeaths.splice(i, 1);
      if (d.bot.killEventSeen) continue;
      this.ctx.events?.emit?.(this.ctx.EVENTS.ENTITY_KILLED, {
        entity: d.bot,
        source: d.source || this.ctx.player,
        weapon: d.source?.weapon || null,
        headshot: !!d.headshot
      });
    }
  }

  // =========================================================================
  // laco
  // =========================================================================

  fixedUpdate(h) {
    if (!this.enabled) return;
    this.time += h;
    this.pathBudget = 1;
    this._flushDeaths();

    this._squadTimer -= h;
    if (this._squadTimer <= 0) {
      this._squadTimer = 0.85;
      this._coordinate();
    }

    // --- cerebro em rodizio -------------------------------------------------
    const bots = this.bots;
    const n = bots.length;
    if (n > 0) {
      let budget = THINK_PER_STEP;
      for (let k = 0; k < n && budget > 0; k++) {
        this._cursor = (this._cursor + 1) % n;
        const b = bots[this._cursor];
        if (!b.active || !b.alive) continue;
        const dt = this.time - b._lastThink;
        if (dt < 0.001) continue;
        b._lastThink = this.time;
        budget--;
        this._safe(b, 'think', dt);
      }
      // garantia de taxa minima: ninguem fica cego porque a fila esta cheia
      for (let k = 0; k < n; k++) {
        const b = bots[k];
        if (!b.active || !b.alive) continue;
        const dt = this.time - b._lastThink;
        if (dt > MAX_THINK_GAP) {
          b._lastThink = this.time;
          this._safe(b, 'think', dt);
        }
      }
    }

    // --- movimento e disparo de todos --------------------------------------
    for (let i = 0; i < n; i++) {
      const b = bots[i];
      if (b.active) this._safe(b, 'step', h);
    }
  }

  update(dt) {
    if (!this.enabled) return;
    const bots = this.bots;
    for (let i = 0; i < bots.length; i++) {
      const b = bots[i];
      if (b.active) this._safe(b, 'updateVisual', dt);
    }
    this._updateCorpses();
    this._updateShadows();
  }

  /**
   * Poe um decalque de sombra sob cada bot. Zero alocacao: matriz, quaternion e
   * vetores sao temporarios de modulo, e setMatrixAt escreve direto no buffer.
   *
   * A altura do chao sai de graca: quando o bot esta APOIADO, o proprio
   * `position` dele JA e o chao (capsuleMove poe os pes nele). So guardamos o
   * ultimo valor apoiado para o caso de ele estar no ar - nenhum raycast novo.
   *
   * A opacidade nao pode variar por instancia sem shader proprio, entao quem faz
   * o esmaecimento e a ESCALA: no ar a mancha encolhe ate sumir, que e como o
   * olho le "descolou do chao" de qualquer jeito.
   */
  _updateShadows() {
    const mesh = this._shadowMesh;
    if (!mesh) return;

    const bots = this.bots;
    const nav = this.navmesh;
    const hasNav = !!(nav && nav.ready);
    const n = Math.min(bots.length, this._shadowCap);

    for (let i = 0; i < n; i++) {
      const b = bots[i];
      let r = 0;

      if (b.active && b.model.root.visible) {
        if (b.grounded) b._shadowY = b.position.y;
        const gy = Number.isFinite(b._shadowY) ? b._shadowY : b.position.y;

        // no ar: encolhe proporcional a altura; acima de SHADOW_AIR nao ha sombra
        const air = b.position.y - gy;
        let k = air > 0 ? 1 - air / SHADOW_AIR : 1;
        if (k > 1) k = 1;
        if (k < 0) k = 0;

        const sink = b.model.sink || 0;
        if (sink > 0) k *= Math.max(0, 1 - sink * 0.9);

        if (k > 0.02) {
          r = SHADOW_RADIUS * k;
          if (b.crouching) r *= 0.9;
          if (!b.alive) r *= 1.25;   // corpo deitado ocupa mais chao que os pes

          _sPos.set(b.position.x, gy + SHADOW_LIFT, b.position.z);
          if (hasNav) nav.slopeAt(b.position.x, b.position.z, _sNormal);
          else _sNormal.set(0, 1, 0);
          _sQuat.setFromUnitVectors(_sUp, _sNormal);
          _sYaw.setFromAxisAngle(_sUp, b.bodyYaw);
          _sQuat.multiply(_sYaw);
          _sScale.set(r * 2, 1, r * 2 * SHADOW_LONG);
          _sMat.compose(_sPos, _sQuat, _sScale);
          mesh.setMatrixAt(i, _sMat);
          continue;
        }
      }

      _sMat.compose(_sZero, _sIdent, _sZero);
      mesh.setMatrixAt(i, _sMat);
    }

    mesh.count = n;
    mesh.instanceMatrix.needsUpdate = true;
  }

  _updateCorpses() {
    for (let i = this._corpses.length - 1; i >= 0; i--) {
      const c = this._corpses[i];
      const age = this.time - c.at;
      if (age < CORPSE_TIME) continue;
      const t = (age - CORPSE_TIME) / CORPSE_SINK;
      if (t >= 1) {
        c.bot.despawn();
        this._corpses.splice(i, 1);
      } else {
        // afunda em vez de sumir de uma vez (piscar objeto na tela e feio)
        c.bot.model.setSink(t * 1.4);
      }
    }
  }

  /**
   * Um bot com defeito degrada em silencio e sai de cena; nunca derruba o frame.
   */
  _safe(bot, method, arg) {
    try {
      bot[method](arg);
    } catch (err) {
      bot._errors = (bot._errors || 0) + 1;
      if (!this._warned) {
        this._warned = true;
        console.warn(`[BotManager] bot ${bot.id} falhou em ${method}:`, err);
      }
      if (bot._errors > 3) {
        const idx = this.aliveList.indexOf(bot);
        if (idx >= 0) this.aliveList.splice(idx, 1);
        bot.despawn();
      }
    }
  }

  // =========================================================================
  // coordenacao de esquadrao
  // =========================================================================

  /**
   * Distribui papeis entre quem esta engajado:
   *   assalto   - ate 2 bots pressionam de frente
   *   flanco    - ate 2 bots contornam, um por lado (nunca os dois pelo mesmo)
   *   supressao - o resto segura distancia, usa cobertura e atira em rajada
   * Sem isso, todos escolhem a mesma rota e o combate vira fila.
   */
  _coordinate() {
    const ppos = this.ctx.player?.position || this.ctx.controller?.position;
    const list = this._sorted;
    list.length = 0;
    if (!ppos) return;

    for (const b of this.aliveList) {
      if (!b.alerted && !b.canSee) { b.role = 'assault'; continue; }
      const dx = b.position.x - ppos.x;
      const dz = b.position.z - ppos.z;
      b._sortKey = dx * dx + dz * dz;
      list.push(b);
    }
    if (list.length === 0) return;
    list.sort(this._cmpDist);

    let flankers = 0;
    for (let i = 0; i < list.length; i++) {
      const b = list[i];
      if (i < 2) {
        b.role = 'assault';
        b.hasFlankPoint = false;
        continue;
      }
      // so manda flanquear quem ainda tem folga de vida e municao
      const canFlank = flankers < 2 && b.health > b.maxHealth * 0.5 && b.ammo > b.weapon.mag * 0.3;
      if (canFlank && this._computeFlank(b, ppos, this._flankSide)) {
        b.role = 'flank';
        flankers++;
        this._flankSide = -this._flankSide;   // proximo vai pelo outro lado
        continue;
      }
      b.role = 'suppress';
      b.hasFlankPoint = false;
    }
  }

  /** Ponto a meio caminho, deslocado para o lado: rota de contorno, nao reta. */
  _computeFlank(bot, ppos, side) {
    if (!this.navmesh.ready) return false;
    const dx = bot.position.x - ppos.x;
    const dz = bot.position.z - ppos.z;
    const d = Math.hypot(dx, dz);
    if (d < 6 || d > 55) return false;
    const rx = dx / d, rz = dz / d;
    const px = -rz * side, pz = rx * side;
    const arc = 9 + Math.random() * 5;

    _v2.set(
      ppos.x + rx * d * 0.62 + px * arc,
      ppos.y,
      ppos.z + rz * d * 0.62 + pz * arc
    );
    this.navmesh.snap(_v2, _v2, 7);

    // ponto tem que ser alcancavel a partir de onde o bot esta
    const rBot = this.navmesh.regionAt(bot.position.x, bot.position.z, bot.position.y);
    const rTgt = this.navmesh.regionAt(_v2.x, _v2.z, _v2.y);
    if (rBot < 0 || rBot !== rTgt) return false;

    const ddx = _v2.x - bot.position.x;
    const ddz = _v2.z - bot.position.z;
    if (ddx * ddx + ddz * ddz < 9) return false; // ja esta la, nao vale flanco

    bot.flankPoint.copy(_v2);
    bot.hasFlankPoint = true;
    return true;
  }
}

export { BOT_STATE };
